import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import {
  StatisticPanels,
  StatisticPanelsProps,
} from "../../../Components/StaticticsPanels/StatisticsPanel";
import { Card } from "primereact/card";
import {
  buildQueryString,
  parseBooleanParam,
  parseStringArrayParam,
} from "../../../utils/urlFilters";
import {
  isThirdPartyClaimPresent,
  parseClaimsArray,
  resolveScorecardAssetId,
} from "../Components/Utils/claimsUtils";
import { ClaimDetail, RowData } from "../Interfaces/interfaces";
import { apiService } from "../../../api/api";
import ClaimsAdsTable from "../Components/Tables/claimsAdsTable";
import {
  ChannelManagerOption,
  claimsAdsFilterOptions,
} from "../Objects/multiSelectObjects";
import { getPrivacy } from "../Components/FilterBars/auditFilters";
import { claimsAdsExportRows } from "../Objects/exportDataObjects";
import AuditFilters from "../Components/FilterBars/auditFilters";
import { Skeleton } from "primereact/skeleton";
import { useYouTubeScorecardData } from "../../../hooks/useYouTubeScorecardData";
import { useYouTubeScorecardFilters } from "../../../hooks/useYouTubeScorecardFilters";
import { trackToolAction } from "../../../utils/analytics";
import { claimsAdsFilterOptions as filterOptions } from "../Objects/filterOptions";
import LoadingState from "../Components/LoadingState";
import ErrorState from "../Components/ErrorState";

// Define the context type for the outlet
interface OutletContext {
  channelManagerOptionsForFilter: ChannelManagerOption[];
}

const ClaimsAdsTab: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get channel manager data from parent route context
  const outletCtx = useOutletContext<OutletContext | undefined>();
  const channelManagerOptionsForFilter =
    outletCtx?.channelManagerOptionsForFilter ?? ([] as ChannelManagerOption[]);

  // Use custom hooks for data and filtering
  const {
    data: rows,
    loading,
    error,
    refreshData,
  } = useYouTubeScorecardData("claimsAds");
  const {
    filters,
    filteredRows,
    myIssuesRows,
    myIssuesOnly,
    handleFilterChange,
    applyFilters,
  } = useYouTubeScorecardFilters(rows);


  const [claimsDetailsForExport, setClaimsDetailsForExport] = useState<
    Record<string, ClaimDetail[]>
  >({});

  const [syncClaimsDetailsForExport, setSyncClaimsDetailsForExport] = useState<
    Record<string, ClaimDetail[]>
  >({});
  const claimsDetailsForExportRef = useRef(claimsDetailsForExport);
  const syncClaimsDetailsForExportRef = useRef(syncClaimsDetailsForExport);
  const assetTitlesByAssetIdRef = useRef<Record<string, string>>({});
  const assetTitlesByVideoIdRef = useRef<Record<string, string>>({});

  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      videoId: parseStringArrayParam(params, "videoId"),
      cms: parseStringArrayParam(params, "cms"),
      channel: parseStringArrayParam(params, "channel"),
      channelId: parseStringArrayParam(params, "channelId"),
      channelManager: parseStringArrayParam(params, "channelManager"),
      issue: parseStringArrayParam(params, "issue"),
      privacyStatus: parseStringArrayParam(params, "privacyStatus"),
      contentFormat: parseStringArrayParam(params, "contentFormat"),
      legalEntity: parseStringArrayParam(params, "legalEntity"),
      legalEntityType: parseStringArrayParam(params, "legalEntityType"),
      myIssuesOnly: parseBooleanParam(params, "myIssuesOnly"),
    };
  }, [location.search]);

  const transformRowForExport = useCallback(
    (row: RowData) => {
      return claimsAdsExportRows(
        row,
        claimsDetailsForExportRef.current,
        syncClaimsDetailsForExportRef.current,
        assetTitlesByAssetIdRef.current,
        assetTitlesByVideoIdRef.current
      );
    },
    []
  );

  const prepareExport = useCallback(async (exportRows: RowData[]) => {
    const videoIds = Array.from(
      new Set(
        exportRows
          .filter((row) => {
            if (!row.videoId) return false;
            const hasThirdPartyDetails =
              parseClaimsArray(row.thirdPartyClaims).length > 0 ||
              parseClaimsArray(claimsDetailsForExportRef.current[row.videoId])
                .length > 0;
            const hasSyncDetails =
              parseClaimsArray(row.syncThirdPartyClaims).length > 0 ||
              parseClaimsArray(syncClaimsDetailsForExportRef.current[row.videoId])
                .length > 0;
            const needsThirdParty =
              (row.thirdPartyClaim === "Yes" ||
                isThirdPartyClaimPresent(row)) &&
              !hasThirdPartyDetails;
            const needsSync =
              row.syncThirdPartyClaim === "Yes" && !hasSyncDetails;
            return needsThirdParty || needsSync;
          })
          .map((row) => String(row.videoId))
      )
    );

    if (videoIds.length > 0) {
      const { detailsByVideoId } =
        await apiService.youtubeScorecard.getYouTubeScorecardClaimsAdsClaimDetailsBulk(
          videoIds
        );

      const nextClaims = { ...claimsDetailsForExportRef.current };
      const nextSyncClaims = { ...syncClaimsDetailsForExportRef.current };

      for (const [videoId, details] of Object.entries(detailsByVideoId || {})) {
        const thirdParty = parseClaimsArray(details.thirdPartyClaims);
        const syncClaims = parseClaimsArray(details.syncThirdPartyClaims);
        if (thirdParty.length > 0) nextClaims[videoId] = thirdParty;
        if (syncClaims.length > 0) nextSyncClaims[videoId] = syncClaims;
      }

      claimsDetailsForExportRef.current = nextClaims;
      syncClaimsDetailsForExportRef.current = nextSyncClaims;
      setClaimsDetailsForExport(nextClaims);
      setSyncClaimsDetailsForExport(nextSyncClaims);
    }

    // Resolve Asset Title: video→AssetTitle (primary, same as copy) + asset ID
    // for third-party / sync claim titles.
    const assetIds = new Set<string>();
    const videoIdsForTitles = new Set<string>();
    for (const row of exportRows) {
      if (row.videoId) videoIdsForTitles.add(String(row.videoId));
      const resolved = resolveScorecardAssetId(row);
      for (const id of resolved.split(",")) {
        const trimmed = id.trim();
        if (trimmed && trimmed !== "Missing") assetIds.add(trimmed);
      }
      const thirdParty =
        claimsDetailsForExportRef.current[row.videoId] ||
        (parseClaimsArray(row.thirdPartyClaims) as ClaimDetail[]);
      const syncClaims =
        syncClaimsDetailsForExportRef.current[row.videoId] ||
        (parseClaimsArray(row.syncThirdPartyClaims) as ClaimDetail[]);
      for (const claim of [...thirdParty, ...syncClaims]) {
        const id =
          claim?.assetId == null ? "" : String(claim.assetId).trim();
        if (id && id !== "Missing") assetIds.add(id);
      }
    }

    const idsToResolve = Array.from(assetIds);
    const videosToResolve = Array.from(videoIdsForTitles);
    if (idsToResolve.length === 0 && videosToResolve.length === 0) return;

    try {
      const mergedAssets: Record<string, string> = {
        ...assetTitlesByAssetIdRef.current,
      };
      const mergedVideos: Record<string, string> = {
        ...assetTitlesByVideoIdRef.current,
      };
      const chunkSize = 500;
      const videoChunks = Math.ceil(videosToResolve.length / chunkSize) || 0;
      const assetChunks = Math.ceil(idsToResolve.length / chunkSize) || 0;
      const totalChunks = Math.max(videoChunks, assetChunks, 1);

      for (let i = 0; i < totalChunks; i++) {
        const videoChunk = videosToResolve.slice(
          i * chunkSize,
          (i + 1) * chunkSize
        );
        const assetChunk = idsToResolve.slice(
          i * chunkSize,
          (i + 1) * chunkSize
        );
        if (videoChunk.length === 0 && assetChunk.length === 0) continue;
        const { titlesByAssetId, titlesByVideoId } =
          await apiService.youtubeScorecard.resolveAssetTitles(
            assetChunk,
            videoChunk
          );
        Object.assign(mergedAssets, titlesByAssetId || {});
        Object.assign(mergedVideos, titlesByVideoId || {});
      }
      assetTitlesByAssetIdRef.current = mergedAssets;
      assetTitlesByVideoIdRef.current = mergedVideos;
    } catch (err) {
      console.error("Failed to resolve asset titles for export:", err);
    }
  }, []);

  // Apply filters when rows or filters change
  useEffect(() => {
    applyFilters(filters, filterOptions);
  }, [rows, filters, applyFilters]);

  const updateUrlFromFilters = useCallback(
    (nextFilters: any) => {
      const isSelected = (value: any) =>
        value === true || value?.checked === true;
      const selectedChannelNames = Object.entries(
        nextFilters.selectedChannels || {}
      )
        .filter(([key, value]) => key.startsWith("channel___") && isSelected(value))
        .map(([key]) => key.replace("channel___", ""));
      const selectedIssueKeys = Object.entries(nextFilters.selectedFilters || {})
        .filter(([_, value]) => isSelected(value))
        .map(([key]) => key);

      const query = buildQueryString({
        videoId: nextFilters.selectedVideoID,
        cms: nextFilters.selectedCMS,
        channel: selectedChannelNames,
        channelId: nextFilters.selectedChannelID,
        channelManager: nextFilters.selectedChannelManagers,
        issue: selectedIssueKeys,
        privacyStatus: nextFilters.selectedPrivacyStatus,
        contentFormat: nextFilters.selectedContentFormat,
        legalEntity: nextFilters.selectedLegalEntity,
        legalEntityType: nextFilters.selectedLegalEntityType,
        myIssuesOnly: nextFilters.myIssuesOnly,
      });

      navigate({ search: query }, { replace: true });
    },
    [navigate]
  );

  const handleSearch = useCallback(
    (nextFilters: any) => {
      trackToolAction("YouTubeScorecard", "search");
      handleFilterChange(nextFilters);
      updateUrlFromFilters(nextFilters);
    },
    [handleFilterChange, updateUrlFromFilters]
  );

  const handleReset = useCallback(
    (nextFilters: any) => {
      handleFilterChange(nextFilters);
      navigate({ search: "" }, { replace: true });
    },
    [handleFilterChange, navigate]
  );

  // Parse claims details for export when the list payload still includes JSON.
  useEffect(() => {
    const parsedClaims: Record<string, ClaimDetail[]> = {};
    rows.forEach((row) => {
      const claims = parseClaimsArray(row.thirdPartyClaims);
      if (claims.length > 0) parsedClaims[row.videoId] = claims;
    });
    claimsDetailsForExportRef.current = parsedClaims;
    setClaimsDetailsForExport(parsedClaims);
  }, [rows]);

  useEffect(() => {
    const parsedSyncClaims: Record<string, ClaimDetail[]> = {};
    rows.forEach((row) => {
      const claims = parseClaimsArray(row.syncThirdPartyClaims);
      if (claims.length > 0) parsedSyncClaims[row.videoId] = claims;
    });
    syncClaimsDetailsForExportRef.current = parsedSyncClaims;
    setSyncClaimsDetailsForExport(parsedSyncClaims);
  }, [rows]);

  const [statsCards, setStatsCards] = useState<StatisticPanelsProps>({
    cards: [
      {
        header: "Total Issues",
        tooltip: "Count of unique videos with any issues",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "totalIssuesClaimsAds",
      },
      // {
      //   header: "Asset Ownership",
      //   tooltip: "Moonbug owned YouTube assets missing ownership",
      //   value: <Skeleton />,
      //   difference: <Skeleton />,
      //   id: "unownedAssetsClaimsAds",
      // },
      {
        header: "Third Party Claims",
        tooltip: "Videos which have been claimed by a third party",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "thirdPartyClaimsClaimsAds",
      },
      {
        header: "Sync Composition Claims",
        tooltip: "Videos which have been claimed by a third party for sync composition rights",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "syncThirdPartyClaimsClaimsAds",
      },
      {
        header: "Unclaimed Videos",
        tooltip: "Videos which have not been claimed and monetized by us",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "unclaimedVideosClaimsAds",
      },
      {
        header: "Missing Pre-Rolls",
        tooltip: "Videos missing Pre-Rolls",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "missingPreRollsClaimsAds",
      },
      {
        header: "Missing Mid-Rolls",
        tooltip: "Videos missing Mid-Rolls (unless on the exceptions list)",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "missingMidRollsClaimsAds",
      },
      {
        header: "Missing Post-Rolls",
        tooltip: "Videos missing Post-Rolls",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "missingPostRollsClaimsAds",
      },
      {
        header: "Mid-Roll Frequency",
        tooltip: "Average mid-roll frequency <2mins or >6mins",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "midRollFrequencyClaimsAds",
      },
      {
        header: "Mid-Roll Interval",
        tooltip: "Largest outlier with mid-rolls interval <2mins or >6mins",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "midRollIntervalClaimsAds",
      },
      {
        header: "Auto Generated Ads",
        tooltip: "Videos with auto-generated ads not enabled",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "autoGeneratedAds",
      },
    ],
  });

  // Update stats when rows change
  useEffect(() => {
    if (rows.length === 0) return;

    // const unownedAssets = rows.filter((row) => row.ownership === "No");
    const thirdParty = rows.filter((row) => isThirdPartyClaimPresent(row));
    const syncThirdParty = rows.filter((row) => row.syncThirdPartyClaim === "Yes");
    const unclaimedVideos = rows.filter((row) => row.claimStatus === "No");
    const preRollConflicts = rows.filter((row) =>
      filterOptions.missingPreRoll(row)
    );
    const midRollConflicts = rows.filter((row) =>
      filterOptions.missingMidRolls(row)
    );
    const postRollConflicts = rows.filter((row) =>
      filterOptions.missingPostRolls(row)
    );
    const midRollFrequency = rows.filter((row) =>
      filterOptions.midRollFrequency(row)
    );
    const midRollInterval = rows.filter((row) =>
      filterOptions.midRollInterval(row)
    );
    const autoGeneratedAds = rows.filter((row) =>
      filterOptions.autoGeneratedAds(row)
    );

    setStatsCards({
      cards: [
        {
          header: "Total Issues",
          tooltip: "Count of unique videos with any issues",
          value: rows.length.toString(),
          difference: [],
          id: "totalIssuesClaimsAds",
        },
        // {
        //   header: "Asset Ownership",
        //   tooltip:
        //     "Moonbug-owned YouTube assets where ownership is not 'Worldwide'",
        //   value: unownedAssets.length.toString(),
        //   difference: [],
        //   id: "unownedAssetsClaimsAds",
        // },
        {
          header: "Third Party Claims",
          tooltip:
            "Videos that have an active content ID claim from another entity",
          value: thirdParty.length.toString(),
          difference: [],
          id: "thirdPartyClaimsClaimsAds",
        },
        {
          header: "Sync Composition Claims",
          tooltip:
            "Videos that have an active sync composition claim from another entity",
          value: syncThirdParty.length.toString(),
          difference: [],
          id: "syncThirdPartyClaimsClaimsAds",
        },
        {
          header: "Unclaimed Videos",
          tooltip: "Videos that haven't been claimed and monetized by Moonbug",
          value: unclaimedVideos.length.toString(),
          difference: [],
          id: "unclaimedVideosClaimsAds",
        },
        {
          header: "Missing Pre-Rolls",
          tooltip: "Videos missing pre-roll ads",
          value: preRollConflicts.length.toString(),
          difference: [],
          id: "missingPreRollsClaimsAds",
        },
        {
          header: "Missing Mid-Rolls",
          tooltip: "Videos missing mid-roll ads",
          value: midRollConflicts.length.toString(),
          difference: [],
          id: "missingMidRollsClaimsAds",
        },
        {
          header: "Missing Post-Rolls",
          tooltip: "Videos missing post-roll ads",
          value: postRollConflicts.length.toString(),
          difference: [],
          id: "missingPostRollsClaimsAds",
        },
        {
          header: "Mid-Roll Frequency",
          tooltip:
            "Videos where the average time between mid-roll ads is either too short or too long",
          value: midRollFrequency.length.toString(),
          difference: [],
          id: "midRollFrequencyClaimsAds",
        },
        {
          header: "Mid-Roll Interval",
          tooltip:
            "Videos where the time between any inidividual mid-roll is either too long or too short",
          value: midRollInterval.length.toString(),
          difference: [],
          id: "midRollIntervalClaimsAds",
        },
        {
          header: "Auto Generated Ads",
          tooltip: "Videos with auto-generated ads not enabled",
          value: autoGeneratedAds.length.toString(),
          difference: [],
          id: "autoGeneratedAdsClaimsAds",
        },
      ],
    });
  }, [rows]);

  const lastUpdated = rows[0]?.lastUpdated;

  // Show loading state
  if (loading) {
    return <LoadingState message="Loading claims and ads data..." />;
  }

  // Show error state if data fetching failed
  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={refreshData}
        title="Error Loading Claims & Ads Data"
      />
    );
  }

  return (
    <div style={{ width: "100%", marginTop: "-30px", marginBottom: "-30px" }}>
      <div>
        <StatisticPanels cards={statsCards.cards} />
        <Card
          className="flex-column md:flex-col gap-3"
          style={{
            marginTop: "0.3rem",
            marginBottom: "2rem",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
            borderRadius: "7px",
          }}
        >
          <AuditFilters
            rows={rows}
            onFilterChange={handleFilterChange}
            onSearch={handleSearch}
            onReset={handleReset}
            issueSelction={claimsAdsFilterOptions}
            exportColumns={transformRowForExport}
            prepareExport={prepareExport}
            filteredRows={filteredRows}
            disabled={myIssuesRows.length <= 0}
            exportName={"Claims & Ads Audit"}
            myIssuesOnly={myIssuesOnly}
            channelManagerOptions={channelManagerOptionsForFilter}
            initialVideoId={initialFilters.videoId}
            initialCMS={initialFilters.cms}
            initialChannelNames={initialFilters.channel}
            initialChannelId={initialFilters.channelId}
            initialChannelManagers={initialFilters.channelManager}
            initialIssueKeys={initialFilters.issue}
            initialPrivacyStatus={initialFilters.privacyStatus}
            initialContentFormat={initialFilters.contentFormat}
            initialLegalEntity={initialFilters.legalEntity}
            initialLegalEntityType={initialFilters.legalEntityType}
            initialMyIssuesOnly={initialFilters.myIssuesOnly}
          />
          <ClaimsAdsTable
            rows={filteredRows}
            loading={loading}
            getPrivacy={getPrivacy}
            transformRowForExport={transformRowForExport}
            lastUpdated={lastUpdated}
          />
        </Card>
      </div>
    </div>
  );
};

export default ClaimsAdsTab;
